import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import {
  getGetEventsQueryOptions, getGetEventQueryOptions, getGetFaceAttributeSamplesQueryOptions,
  getGetFaceAttributesQueryOptions, getGetGroupsQueryOptions, getGetHandlerQueryOptions,
  getGetHandlersQueryOptions, getGetListsQueryOptions, getGetStreamQueryOptions,
  getGetStreamsQueryOptions, getSample, sdk,
} from '@vlabs/api/luna-api/client';
import type { GetEventsParams } from '@vlabs/api/luna-api/model';
import { getGetCardQueryOptions } from '@vlabs/api/point-api/client';
import { queryClient } from '@vlabs/api/queryClient';

import type {
  CardView, EventApi, EventDetectionItemView, EventOption, EventOptionItem,
  EventView, SampleView,
} from './EventApi';
import { buildLunaStreamGroups } from './streamGroups';

const enrichDetections = (
  detections: { sample_id?: string | null; image_origin?: string | null }[] | null | undefined,
  kind: 'faces' | 'bodies',
): EventDetectionItemView[] => (detections ?? []).map((d) => ({
  ...d,
  sample_id: d.sample_id,
  image_origin: d.image_origin,
  uiSampleURL: d.sample_id ? getLunaSampleUrl(kind, d.sample_id) : undefined,
}));

const toEventView = (event: Record<string, unknown>): EventView => ({
  ...event,
  event_id: event.event_id as string | undefined,
  create_time: event.create_time as string | undefined,
  handler_id: event.handler_id as string | undefined,
  stream_id: event.stream_id as string | undefined,
  source: event.source as string | null | undefined,
  location: event.location as EventView['location'],
  tags: event.tags as string[] | null | undefined,
  track_id: event.track_id as string | null | undefined,
  top_match: event.top_match as Record<string, unknown> | null | undefined,
  face_detections: enrichDetections(event.face_detections as never, 'faces'),
  body_detections: enrichDetections(event.body_detections as never, 'bodies'),
  attach_result: event.attach_result as string[] | undefined,
  user_data: event.user_data as string | undefined,
});

const toOptions = (
  items: EventOptionItem[],
  valueKey: 'name' | 'handler_id' | 'list_id',
  labelKey: 'name' | 'description' | 'user_data',
): EventOption[] => items.map((i) => ({
  value: (i[valueKey] ?? i.stream_id ?? ''),
  label: (i[labelKey] ?? i[valueKey] ?? i.stream_id ?? ''),
}));

const toCardView = (card: Record<string, unknown>): CardView => ({
  card_id: (card.card_id ?? card.external_id) as string | undefined,
  external_id: (card.external_id ?? card.card_id) as string | undefined,
  user_data: card.user_data as CardView['user_data'],
});

const toSamples = (
  samples: (string | null | undefined)[] | undefined,
): { samples: SampleView[] } => ({
  samples: (samples ?? []).map((sampleId) => (sampleId ? {
    sample_id: sampleId,
    uiSampleURL: getLunaSampleUrl('faces', sampleId),
  } : {})),
});

export const lunaEventApi: EventApi = {
  getEvents: async (params) => {
    const data = await queryClient.fetchQuery(getGetEventsQueryOptions(params as GetEventsParams));
    return { events: (data?.events ?? []).map((e) => toEventView(e as unknown as Record<string, unknown>)) };
  },
  getEvent: async (eventId) => {
    const event = await queryClient.fetchQuery(getGetEventQueryOptions(eventId));
    return toEventView(event as unknown as Record<string, unknown>);
  },
  getStream: async (streamId) => {
    const stream = await queryClient.fetchQuery(getGetStreamQueryOptions(streamId));
    if (!stream) return undefined;
    return { stream_id: stream.stream_id ?? streamId, name: stream.name };
  },
  getStreamOptions: async () => {
    const data = await queryClient.fetchQuery(getGetStreamsQueryOptions());
    return toOptions(data?.streams ?? [], 'name', 'name');
  },
  getStreamGroups: async () => {
    const [groupsData, streamsData] = await Promise.all([
      queryClient.fetchQuery(getGetGroupsQueryOptions()),
      queryClient.fetchQuery(getGetStreamsQueryOptions()),
    ]);
    return buildLunaStreamGroups(groupsData?.groups ?? [], streamsData?.streams ?? []);
  },
  getHandlerOptions: async () => {
    const handlers = await queryClient.fetchQuery(getGetHandlersQueryOptions());
    return toOptions(handlers ?? [], 'handler_id', 'description');
  },
  getHandler: async (handlerId) => {
    const handler = await queryClient.fetchQuery(getGetHandlerQueryOptions(handlerId));
    return (handler ?? undefined);
  },
  getListOptions: async () => {
    const data = await queryClient.fetchQuery(getGetListsQueryOptions());
    return toOptions(data?.lists ?? [], 'list_id', 'user_data');
  },
  getCard: async (cardId) => {
    const card = await queryClient.fetchQuery(getGetCardQueryOptions(cardId, { targets: ['user_data'] }));
    return toCardView(card as unknown as Record<string, unknown>);
  },
  getFaceAttributeSamples: async (faceId) => {
    const data = await queryClient.fetchQuery(getGetFaceAttributeSamplesQueryOptions(faceId));
    return toSamples(data?.samples);
  },
  getFaceAttributes: async (faceId) => {
    const face = await queryClient.fetchQuery(getGetFaceAttributesQueryOptions(faceId));
    return (face?.attributes?.basic_attributes ?? undefined);
  },
  getSample: async (kind, sampleId) => getSample(kind, sampleId),
  estimateSdk: async (file, params) => {
    const data = await sdk(file, params, { headers: { 'Content-Type': file.type } });
    return { data: data as Record<string, unknown> };
  },
  capabilities: {
    externalIdField: 'external_ids',
    topSimilarCardIds: false,
    videoArchive: true,
  },
};

export { enrichDetections as enrichLunaDetections, toEventView as toLunaEventView };
